import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, delay, finalize, Observable, of, retryWhen, scan } from 'rxjs';
import { ToastService } from '@shared/toast';
import { ChargementService } from '../../components/chargement/chargement.service';
import { CHARGEMENT_GLOBAL } from './chargement-global';
import { erreurPersistante } from '../../toast/erreur-persistante';
import { SUCCES_IMPLICITE } from './succes-implicite';
import { EcrituresEnCoursService } from '../../services/ecritures-en-cours.service';

const MESSAGE_ERREUR_PAR_DEFAUT = 'Une erreur est survenue';

const RETRY_TIMES = 5;
const DELAY = 2000;

// Seules les méthodes sans effet de bord sont rejouées : une commande d'écriture n'est pas
// idempotente (allocation d'index, écriture multi-clés non transactionnelle), un rejeu
// automatique la dupliquerait ou la laisserait à moitié appliquée
const METHODES_REJOUABLES = ['GET', 'HEAD', 'OPTIONS'];

export function httpIntercepteur(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const isLoading = inject(ChargementService);
  const toast = inject(ToastService);
  // Écritures en cours, lues par `pasDEcritureEnCoursGuard`
  const ecritures = inject(EcrituresEnCoursService);
  const estEcriture = !METHODES_REJOUABLES.includes(req.method);

  function handleClientError(error: ErrorEvent, errorsBase: string): string {
    console.log('An error occurred:', error.message);
    return `Client-side error: ${error.message} : ${errorsBase}`;
  }

  function handleHttpError(error: HttpErrorResponse, errorsBase: string): string {
    switch (error.status) {
      case HttpStatusCode.Unauthorized:
        console.error('Unauthorized:', errorsBase);
        return `Unauthorized: ${errorsBase}`;
      case HttpStatusCode.Forbidden:
        console.error('Forbidden:', errorsBase);
        return `Forbidden: ${errorsBase}`;
      case HttpStatusCode.NotFound:
        console.error('Not Found:', errorsBase);
        return `Not Found: ${errorsBase}`;
      case HttpStatusCode.ServiceUnavailable:
        console.error('Service Unavailable:', errorsBase);
        return `Service Unavailable: ${errorsBase}`;
      default:
        console.error(errorsBase || MESSAGE_ERREUR_PAR_DEFAUT);
        return errorsBase || MESSAGE_ERREUR_PAR_DEFAUT;
    }
  }

  function isInstanceOfBlobAndTypeIncludesProblemJson(error: HttpErrorResponse) {
    return error.error instanceof Blob && error.error.type.includes('application/problem+json');
  }

  function handleApplicationProblemJson(error: HttpErrorResponse) {
    return new Observable<never>(observer => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const jsonResponse = JSON.parse(reader.result as string);
          const formattedError = new HttpErrorResponse({
            error: jsonResponse,
            headers: error.headers,
            status: error.status,
            statusText: error.statusText,
            url: error.url!,
          });
          observer.error(formattedError);
        } catch {
          observer.error(error);
        }
      };
      reader.readAsText(error.error);
    }).pipe(
      catchError(err => {
        /* `errors?.[0]` et non `errors[0]` : quand `JSON.parse` du blob echoue, le
           `catch` renvoie l'erreur d'origine dont `.error` est un Blob — `errors`
           est alors `undefined` et l'indexation levait DANS le `catchError`. */
        erreurPersistante(toast, err.error?.errors?.[0]?.description ?? MESSAGE_ERREUR_PAR_DEFAUT);
        return of();
      }),
    );
  }

  function getErrorMessage(error: HttpErrorResponse | Error, errorsBase: string): string {
    if (error instanceof HttpErrorResponse && error.error instanceof ErrorEvent) {
      return handleClientError(error.error, errorsBase);
    } else {
      return handleHttpError(error as HttpErrorResponse, errorsBase);
    }
  }

  let isSuccess: boolean = true;

  // L'appelant qui rend son propre etat d'attente (squelette de tableau) coupe le
  // voile plein ecran, sans quoi les deux indicateurs se superposent.
  const chargementGlobal = req.context.get(CHARGEMENT_GLOBAL);
  const to = chargementGlobal
    ? setTimeout(() => {
        isLoading.isLoading.set(true);
      }, 700)
    : undefined;

  if (estEcriture) {
    ecritures.debut();
  }

  return next(req).pipe(
    retryWhen(errors =>
      errors.pipe(
        scan((retryCount, error) => {
          if (!METHODES_REJOUABLES.includes(req.method)) {
            throw error;
          }
          if (error instanceof HttpErrorResponse && error.status >= 400 && error.status < 500) {
            throw error;
          }
          if (retryCount >= RETRY_TIMES) {
            throw error;
          }
          return retryCount + 1;
        }, 0),
        delay(DELAY),
      ),
    ),

    catchError(error => {
      console.log(error);
      let errorMessage = 'An unexpected error has occurred.';

      if (error instanceof HttpErrorResponse) {
        if (isInstanceOfBlobAndTypeIncludesProblemJson(error)) {
          return handleApplicationProblemJson(error);
        }

        const errorsBase = error.error?.errors
          ? error.error?.errors[0]?.description
          : 'Une erreur est survenue, contactez le support.';

        errorMessage = getErrorMessage(error, errorsBase);
      } else {
        console.error(errorMessage);
      }
      isSuccess = false;
      /* Persistante, comme avant la migration : une erreur HTTP que l'utilisateur
         n'a pas eu le temps de lire est une erreur perdue. */
      erreurPersistante(toast, errorMessage);

      return of();
    }),
    finalize(() => {
      // L'appelant qui affiche son propre message métier désactive le message générique
      // (cf. SUCCES_IMPLICITE) pour ne pas empiler deux alertes sur une seule action
      if (req.context.get(SUCCES_IMPLICITE) && (req.method === 'POST' || req.method === 'PUT') && isSuccess) {
        toast.success(`Votre demande a été traitée avec succès`);
      }
      if (to !== undefined) {
        clearTimeout(to);
        isLoading.isLoading.set(false);
      }
      if (estEcriture) {
        ecritures.fin();
      }
    }),
  );
}
